import { useTranslation } from 'react-i18next';
import { BriefcaseBusiness, UsersRound, MessageSquare, ChevronLeft } from 'lucide-react';
import { useLocale } from '../../hooks/useLocale';
import { useOnboarding, type PostOnboardingRoute } from './OnboardingProvider';

const OPTIONS: Array<{
  id: string;
  icon: typeof BriefcaseBusiness;
  titleKey: string;
  titleDefault: string;
  descKey: string;
  descDefault: string;
  route: PostOnboardingRoute;
}> = [
  {
    id: 'create-job',
    icon: BriefcaseBusiness,
    titleKey: 'onboarding.companyInfo.nextActionCreateJob',
    titleDefault: 'Create a job',
    descKey: 'onboarding.companyInfo.nextActionCreateJobDesc',
    descDefault: 'Post your first role and start collecting applicants.',
    route: '/(employer)/jobs/create',
  },
  {
    id: 'find-candidate',
    icon: UsersRound,
    titleKey: 'onboarding.companyInfo.nextActionFindCandidate',
    titleDefault: 'Find candidates',
    descKey: 'onboarding.companyInfo.nextActionFindCandidateDesc',
    descDefault: 'Browse candidate profiles and shortlist talent quickly.',
    route: '/(employer)/candidates',
  },
  {
    id: 'chat',
    icon: MessageSquare,
    titleKey: 'onboarding.companyInfo.nextActionChat',
    titleDefault: 'Go to recruiter chat',
    descKey: 'onboarding.companyInfo.nextActionChatDesc',
    descDefault: 'Use Tabbio AI to draft roles and recruiting workflows.',
    route: '/(employer)/chat',
  },
];

export function StepCompanyNextAction() {
  const { t } = useTranslation('auth');
  const { isRTL } = useLocale();
  const { postOnboardingRoute, setPostOnboardingRoute, nextStep, prevStep } = useOnboarding();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <div style={{ maxWidth: 560, marginInline: 'auto', width: '100%' }}>
          <button
            onClick={prevStep}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, marginBottom: 12 }}
          >
            <ChevronLeft size={24} color="var(--text-primary)" style={isRTL ? { transform: 'scaleX(-1)' } : undefined} />
          </button>

          <h1 style={{ fontSize: 32, fontWeight: 400, letterSpacing: '-0.01em', color: 'var(--text-primary)', marginBottom: 4 }}>
            {t('onboarding.companyInfo.nextActionTitle', { defaultValue: 'What do you want to do first?' })}
          </h1>
          <p style={{ fontSize: 15, lineHeight: '22px', color: 'var(--text-secondary)', marginBottom: 24 }}>
            {t('onboarding.companyInfo.nextActionSubtitle', {
              defaultValue: "We'll take you there right after onboarding.",
            })}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const selected = postOnboardingRoute === opt.route;
              return (
                <button
                  key={opt.id}
                  onClick={() => setPostOnboardingRoute(opt.route)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: 16,
                    borderRadius: 16,
                    border: `1px solid ${selected ? 'var(--text-primary)' : 'var(--gem-outline-variant)'}`,
                    backgroundColor: selected ? 'rgba(0,0,0,0.04)' : 'transparent',
                    textAlign: 'start',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    transition: 'border-color 150ms, background-color 150ms',
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      backgroundColor: 'rgba(0,0,0,0.04)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={19} color="var(--text-secondary)" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
                      {t(opt.titleKey, { defaultValue: opt.titleDefault })}
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 2 }}>
                      {t(opt.descKey, { defaultValue: opt.descDefault })}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ padding: '12px 24px 24px' }}>
        <div style={{ maxWidth: 560, marginInline: 'auto' }}>
          <button
            type="button"
            onClick={nextStep}
            style={{
              width: '100%',
              height: 48,
              borderRadius: 9999,
              backgroundColor: 'var(--text-primary)',
              color: 'var(--bg)',
              fontSize: 15,
              fontWeight: 500,
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'opacity 150ms',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.88';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            {t('onboarding.continue', { defaultValue: 'Continue' })}
          </button>
        </div>
      </div>
    </div>
  );
}
